import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Layout,
  Card,
  Button,
  Input,
  Table,
  Space,
  Typography,
  message,
  Modal,
  Form,
  InputNumber,
  Spin,
} from 'antd';
import {
  LogoutOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  SaveOutlined,
  MoonOutlined,
  SunOutlined,
  ReloadOutlined,
  EditOutlined,
  LoadingOutlined,
  ColumnWidthOutlined,
  MenuOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { updateCurrencies, updateOfficeSettings } from '../store/slices/exchangeOfficeSlice';
import { Currency, CurrencyRequest, ExchangeOffice, MediaObject } from '../types';
import { defaultCurrencies } from '../utils/defaultCurrencies';
import { useTheme } from '../contexts/ThemeContext';
import * as api from '../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

// Helper function to extract URL from media object or string
const getMediaUrl = (media: MediaObject | string | null | undefined): string => {
  if (!media) return '';
  if (typeof media === 'string') return media;
  return media.url || '';
};

// Helper function to extract ID from media object
const getMediaId = (media: MediaObject | string | null | undefined): number | null => {
  if (!media) return null;
  if (typeof media === 'object' && media.id) return media.id;
  return null;
};

// PWA install prompt hook
function usePwaInstall() {
  const deferredPrompt = useRef<any>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler as any);
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setCanInstall(false);
    }
    return () => window.removeEventListener('beforeinstallprompt', handler as any);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt.current) return false;
    deferredPrompt.current.prompt();
    const result = await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    setCanInstall(false);
    return result.outcome === 'accepted';
  }, []);

  return { canInstall, install };
}

// Check if running as installed PWA
function useIsPwa() {
  const [isPwa, setIsPwa] = useState(false);
  useEffect(() => {
    setIsPwa(window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true);
  }, []);
  return isPwa;
}

// Simple mobile detection
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentOffice: authOffice } = useSelector((state: any) => state.auth);
  const { theme, isDark, toggleTheme } = useTheme();
  const { canInstall, install: installPwa } = usePwaInstall();
  const isPwa = useIsPwa();
  const isMobile = useIsMobile();

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [modifiedCurrencyIds, setModifiedCurrencyIds] = useState<Set<number>>(new Set()); // Track modified rows
  const [updatingCurrencyId, setUpdatingCurrencyId] = useState<number | null>(null); // Track which currency is being updated
  const [currentOffice, setCurrentOffice] = useState<ExchangeOffice | null>(null);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const [installingApp, setInstallingApp] = useState(false);
  const [displaySettings, setDisplaySettings] = useState({
    logo: '',
    video: '',
    videoOption: '',
    // Media IDs for API update
    logoId: null as number | null,
    videoId: null as number | null,
    videoOptionId: null as number | null,
  });
  const [loading, setLoading] = useState(false);
  const [savingCurrencies, setSavingCurrencies] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [form] = Form.useForm();

  // Redirect if not office user
  useEffect(() => {
    const officeToken = localStorage.getItem('office-token');
    if (!officeToken) {
      message.warning('يجب تسجيل الدخول كمكتب صرافة للوصول لهذه الصفحة');
      navigate('/login');
    }
  }, [navigate]);

  // Load office data and currencies
  useEffect(() => {
    loadOfficeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOfficeData = async () => {
    setLoading(true);
    try {
      // Get current office details
      const office = await api.getCurrentOffice();
      console.log('Office data loaded:', office);
      setCurrentOffice(office);

      // Get currencies
      const currenciesData = await api.getOfficeCurrencies();
      const sortedCurrencies = [...currenciesData].sort((a, b) => a.order - b.order);
      setCurrencies(sortedCurrencies);
      setModifiedCurrencyIds(new Set()); // Reset modified tracking

      // Set display settings - extract URL and ID from media objects
      const logoUrl = getMediaUrl(office.logo);
      const videoUrl = getMediaUrl(office.video);
      const videoOptionUrl = getMediaUrl(office.videoOption);
      const logoId = getMediaId(office.logo);
      const videoId = getMediaId(office.video);
      const videoOptionId = getMediaId(office.videoOption);

      console.log('Display settings:', { logoUrl, videoUrl, videoOptionUrl, logoId, videoId, videoOptionId });

      setDisplaySettings({
        logo: logoUrl,
        video: videoUrl,
        videoOption: videoOptionUrl,
        logoId: logoId,
        videoId: videoId,
        videoOptionId: videoOptionId,
      });
    } catch (error: any) {
      console.error('Failed to load office data:', error);
      message.error('فشل في تحميل البيانات: ' + error.message);
      // Fallback to auth office data
      if (authOffice) {
        setCurrentOffice(authOffice);
        const fallbackCurrencies = [...(authOffice.currencies || [])].sort((a, b) => a.order - b.order);
        setCurrencies(fallbackCurrencies);
        setModifiedCurrencyIds(new Set());
        setDisplaySettings({
          logo: getMediaUrl(authOffice.logo),
          video: getMediaUrl(authOffice.video),
          videoOption: getMediaUrl(authOffice.videoOption),
          logoId: getMediaId(authOffice.logo),
          videoId: getMediaId(authOffice.video),
          videoOptionId: getMediaId(authOffice.videoOption),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Reload currencies from API
  const reloadCurrencies = async () => {
    try {
      const currenciesData = await api.getOfficeCurrencies();
      const sortedCurrencies = [...currenciesData].sort((a, b) => a.order - b.order);
      setCurrencies(sortedCurrencies);
      setModifiedCurrencyIds(new Set()); // Reset modified tracking
    } catch (error: any) {
      console.error('Failed to reload currencies:', error);
    }
  };

  // Check if a currency has been modified
  const isCurrencyModified = (currencyId: number): boolean => {
    return modifiedCurrencyIds.has(currencyId);
  };

  // Mark currency as modified
  const markCurrencyModified = (currencyId: number) => {
    setModifiedCurrencyIds(prev => new Set(prev).add(currencyId));
  };

  // Update single currency via API
  const handleUpdateCurrency = async (currency: Currency) => {
    if (currency.id === 0) return; // Skip new currencies that haven't been saved yet

    setUpdatingCurrencyId(currency.id);
    try {
      const currencyRequest: CurrencyRequest = {
        exchangeOfficeId: currentOffice?.id || authOffice?.id,
        flag: currency.flag,
        nameEn: currency.nameEn,
        nameAr: currency.nameAr,
        iso: currency.iso,
        units: '1',
        buy: currency.buy,
        sell: currency.sell,
        order: currency.order,
      };

      await api.updateCurrency(currency.id, currencyRequest);
      message.success('تم تحديث العملة');

      // Reload currencies from API
      await reloadCurrencies();
    } catch (error: any) {
      message.error('فشل في تحديث العملة: ' + error.message);
    } finally {
      setUpdatingCurrencyId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await api.officeLogout();
    } catch (error) {
      // Continue with logout even if API call fails
    }
    dispatch(logout());
    navigate('/login');
  };

  const handleAddCurrency = () => {
    const newCurrency: Currency = {
      id: 0, // Will be assigned by API
      exchangeOfficeId: currentOffice?.id || authOffice?.id || 0,
      flag: '',
      nameEn: '',
      nameAr: '',
      iso: '',
      units: '1',
      buy: 0,
      sell: 0,
      order: currencies.length,
    };
    setEditingCurrency(newCurrency);
    form.setFieldsValue(newCurrency);
    setIsModalVisible(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEditCurrency = (currency: Currency) => {
    setEditingCurrency(currency);
    form.setFieldsValue(currency);
    setIsModalVisible(true);
  };

  const handleDeleteCurrency = async (id: number) => {
    try {
      await api.deleteCurrency(id);
      message.success('تم حذف العملة');
      // Reload currencies from API after deletion
      await reloadCurrencies();
    } catch (error: any) {
      message.error('فشل في حذف العملة: ' + error.message);
    }
  };

  const handleMoveCurrency = async (index: number, direction: 'up' | 'down') => {
    const newCurrencies = [...currencies];
    let currency1: Currency | null = null;
    let currency2: Currency | null = null;

    if (direction === 'up' && index > 0) {
      [newCurrencies[index], newCurrencies[index - 1]] = [
        newCurrencies[index - 1],
        newCurrencies[index],
      ];
      newCurrencies[index].order = index;
      newCurrencies[index - 1].order = index - 1;
      currency1 = newCurrencies[index];
      currency2 = newCurrencies[index - 1];
    } else if (direction === 'down' && index < newCurrencies.length - 1) {
      [newCurrencies[index], newCurrencies[index + 1]] = [
        newCurrencies[index + 1],
        newCurrencies[index],
      ];
      newCurrencies[index].order = index;
      newCurrencies[index + 1].order = index + 1;
      currency1 = newCurrencies[index];
      currency2 = newCurrencies[index + 1];
    }

    // Update UI immediately
    setCurrencies(newCurrencies);

    // Call API to update order for both currencies
    if (currency1 && currency2 && currency1.id !== 0 && currency2.id !== 0) {
      try {
        await Promise.all([
          api.updateCurrency(currency1.id, { order: currency1.order }),
          api.updateCurrency(currency2.id, { order: currency2.order }),
        ]);
        message.success('تم تحديث الترتيب');
        // Reload to ensure sync with server
        await reloadCurrencies();
      } catch (error: any) {
        message.error('فشل في تحديث الترتيب: ' + error.message);
        // Reload to revert to server state
        await reloadCurrencies();
      }
    }
  };

  const handleSaveCurrency = async () => {
    try {
      const values = await form.validateFields();

      if (editingCurrency) {
        const currencyRequest: CurrencyRequest = {
          exchangeOfficeId: currentOffice?.id || authOffice?.id,
          flag: values.flag,
          nameEn: values.nameEn,
          nameAr: values.nameAr,
          iso: values.iso,
          units: '1',
          buy: values.buy,
          sell: values.sell,
          order: editingCurrency.order,
        };

        if (editingCurrency.id === 0) {
          // Create new currency
          await api.createCurrency(currencyRequest);
          message.success('تم إضافة العملة');
        } else {
          // Update existing currency
          await api.updateCurrency(editingCurrency.id, currencyRequest);
          message.success('تم تحديث العملة');
        }

        // Reload currencies from API
        await reloadCurrencies();
        setIsModalVisible(false);
      }
    } catch (error: any) {
      message.error('فشل في حفظ العملة: ' + error.message);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      // Use updateCurrentOffice API with media IDs
      const updatedOffice = await api.updateCurrentOffice({
        logoId: displaySettings.logoId,
        logo_id: displaySettings.logoId,
        videoId: displaySettings.videoId,
        video_id: displaySettings.videoId,
        videoOptionId: displaySettings.videoOptionId,
        video_option_id: displaySettings.videoOptionId,
      });

      // Update current office state with new data
      setCurrentOffice(updatedOffice);
      
      // Update display settings from response
      setDisplaySettings({
        logo: getMediaUrl(updatedOffice.logo),
        video: getMediaUrl(updatedOffice.video),
        videoOption: getMediaUrl(updatedOffice.videoOption),
        logoId: getMediaId(updatedOffice.logo),
        videoId: getMediaId(updatedOffice.video),
        videoOptionId: getMediaId(updatedOffice.videoOption),
      });

      dispatch(updateOfficeSettings({
        officeId: currentOffice?.id || authOffice?.id,
        logo: getMediaUrl(updatedOffice.logo),
        video: getMediaUrl(updatedOffice.video),
        videoOption: getMediaUrl(updatedOffice.videoOption),
      }));
      message.success('تم حفظ الإعدادات');
    } catch (error: any) {
      message.error('فشل في حفظ الإعدادات: ' + error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveAllCurrencies = async () => {
    setSavingCurrencies(true);
    try {
      const officeId = currentOffice?.id || authOffice?.id;

      // Update order for all currencies
      const currencyRequests: CurrencyRequest[] = currencies.map((c, index) => ({
        exchangeOfficeId: officeId,
        flag: c.flag,
        nameEn: c.nameEn,
        nameAr: c.nameAr,
        iso: c.iso,
        units: '1',
        buy: c.buy,
        sell: c.sell,
        order: index,
      }));

      // Use bulk create/update API
      await api.createCurrencies(currencyRequests);
      message.success('تم حفظ جميع العملات');

      // Reload currencies from API
      await reloadCurrencies();

      if (officeId) {
        dispatch(updateCurrencies({ officeId, currencies }));
      }
    } catch (error: any) {
      message.error('فشل في حفظ العملات: ' + error.message);
    } finally {
      setSavingCurrencies(false);
    }
  };

  // تعيين القيم الافتراضية وحفظها مباشرة بدون الحاجة لزر حفظ
  const handleResetDefaults = async () => {
    setSavingCurrencies(true);
    try {
      const officeId = currentOffice?.id || authOffice?.id;
      const currencyRequests: CurrencyRequest[] = defaultCurrencies.map((c, index) => ({
        exchangeOfficeId: officeId,
        flag: c.flag,
        nameEn: c.nameEn,
        nameAr: c.nameAr,
        iso: c.iso,
        units: '1',
        buy: c.buy,
        sell: c.sell,
        order: index,
      }));

      await api.createCurrencies(currencyRequests);
      message.success('تم تعيين وحفظ القيم الافتراضية بنجاح');

      // Reload currencies from API
      await reloadCurrencies();

      if (officeId) {
        dispatch(updateCurrencies({ officeId, currencies }));
      }
    } catch (error: any) {
      message.error('فشل في تعيين القيم الافتراضية: ' + error.message);
    } finally {
      setSavingCurrencies(false);
    }
  };

  const handleRefresh = () => {
    loadOfficeData();
  };


  // Helper to update currency and mark as modified
  const updateCurrencyField = (recordId: number, field: string, value: any) => {
    const updated = currencies.map((c) =>
      c.id === recordId ? { ...c, [field]: value } : c
    );
    setCurrencies(updated);
    if (recordId !== 0) {
      markCurrencyModified(recordId);
    }
  };

  const columns = [
    {
      title: 'FLAG',
      dataIndex: 'flag',
      key: 'flag',
      width: 100,
      render: (text: string, record: Currency) => (
        <Input
          value={text}
          onChange={(e) => updateCurrencyField(record.id, 'flag', e.target.value)}
          placeholder="us, gb, etc."
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: 'NAME (EN)',
      dataIndex: 'nameEn',
      key: 'nameEn',
      render: (text: string, record: Currency) => (
        <Input
          value={text}
          onChange={(e) => updateCurrencyField(record.id, 'nameEn', e.target.value)}
        />
      ),
    },
    {
      title: 'NAME (AR)',
      dataIndex: 'nameAr',
      key: 'nameAr',
      render: (text: string, record: Currency) => (
        <Input
          value={text}
          onChange={(e) => updateCurrencyField(record.id, 'nameAr', e.target.value)}
          style={{ direction: 'rtl' }}
        />
      ),
    },
    {
      title: 'ISO',
      dataIndex: 'iso',
      key: 'iso',
      width: 100,
      render: (text: string, record: Currency) => (
        <Input
          value={text}
          onChange={(e) => updateCurrencyField(record.id, 'iso', e.target.value)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: 'BUY',
      dataIndex: 'buy',
      key: 'buy',
      width: 120,
      render: (value: number, record: Currency, rowIndex: number) => (
        <div className="admin-rate-cell admin-rate-buy" style={{ animationDelay: `${rowIndex * 0.15}s` }}>
          <InputNumber
            value={value}
            onChange={(val) => updateCurrencyField(record.id, 'buy', val || 0)}
            min={0}
            step={0.01}
            style={{ width: 110 }}
          />
        </div>
      ),
    },
    {
      title: 'SELL',
      dataIndex: 'sell',
      key: 'sell',
      width: 120,
      render: (value: number, record: Currency, rowIndex: number) => (
        <div className="admin-rate-cell admin-rate-sell" style={{ animationDelay: `${rowIndex * 0.15}s` }}>
          <InputNumber
            value={value}
            onChange={(val) => updateCurrencyField(record.id, 'sell', val || 0)}
            min={0}
            step={0.01}
            style={{ width: 110 }}
          />
        </div>
      ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 200,
      render: (_: any, record: Currency, index: number) => (
        <Space>
          {/* Show update button only if currency is modified and has valid ID */}
          {isCurrencyModified(record.id) && record.id !== 0 && (
            <Button
              type="primary"
              icon={updatingCurrencyId === record.id ? <LoadingOutlined /> : <EditOutlined />}
              onClick={() => handleUpdateCurrency(record)}
              disabled={updatingCurrencyId === record.id}
              size="small"
              style={{ background: theme.success, borderColor: theme.success }}
            >

            </Button>
          )}
          <Button
            icon={<ArrowUpOutlined />}
            onClick={() => handleMoveCurrency(index, 'up')}
            disabled={index === 0}
            size="small"
          />
          <Button
            icon={<ArrowDownOutlined />}
            onClick={() => handleMoveCurrency(index, 'down')}
            disabled={index === currencies.length - 1}
            size="small"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCurrency(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: theme.background, color: theme.text }}>
      <Modal
        title="تنزيل / تثبيت التطبيق"
        open={installHelpOpen}
        onCancel={() => setInstallHelpOpen(false)}
        footer={[
          <Button key="close" onClick={() => setInstallHelpOpen(false)}>
            إغلاق
          </Button>,
        ]}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>
            إذا لم يظهر التثبيت تلقائيًا، يمكنك تثبيت التطبيق يدويًا من المتصفح:
          </Text>
          <Text strong>Android (Chrome):</Text>
          <Text>افتح قائمة ⋮ ثم اختر “Install app” أو “Add to Home screen”.</Text>
          <Text strong>iPhone/iPad (Safari):</Text>
          <Text>اضغط زر المشاركة ثم اختر “Add to Home Screen”.</Text>
          <Text type="secondary">هذا التثبيت يعمل كـPWA (بدون APK/IPA) من المتصفح مباشرة.</Text>
        </Space>
      </Modal>
      <Header style={{
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        padding: isMobile ? '8px 12px' : '0 24px',
        height: isMobile ? 'auto' : undefined,
        gap: isMobile ? 8 : 0,
      }}>
        <Title level={isMobile ? 5 : 3} style={{ color: theme.text, margin: 0, textAlign: isMobile ? 'center' : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {currentOffice?.name || authOffice?.name || 'لوحة التحكم'}
          {!isMobile && ' • Dashboard'}
        </Title>
        <Space wrap size={isMobile ? 'small' : 'middle'} style={{ justifyContent: isMobile ? 'center' : undefined }}>
          {/* App Download / Install Button (PWA) */}
          {!isPwa && (
            <Button
              icon={<DownloadOutlined />}
              loading={installingApp}
              onClick={async () => {
                if (installingApp) return;
                try {
                  setInstallingApp(true);
                  if (canInstall) {
                    const ok = await installPwa();
                    if (ok) message.success('تم تثبيت التطبيق بنجاح!');
                    else message.info('تم إلغاء التثبيت.');
                  } else {
                    setInstallHelpOpen(true);
                  }
                } finally {
                  setInstallingApp(false);
                }
              }}
              style={{ background: '#22c55e', borderColor: '#22c55e', color: '#fff', fontWeight: 700 }}
            >
              {isMobile ? 'تنزيل' : 'تنزيل التطبيق'}
            </Button>
          )}
          <Button
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
            style={{ background: theme.surfaceLight, borderColor: theme.border, color: theme.text }}
          >
            {isMobile ? '' : (isDark ? 'فاتح' : 'داكن')}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            style={{ background: theme.info, borderColor: theme.info, color: '#fff' }}
          >
            {isMobile ? '' : 'تحديث'}
          </Button>
          <Button icon={<LogoutOutlined />} onClick={handleLogout} danger>
            {isMobile ? '' : 'تسجيل الخروج'}
          </Button>
        </Space>
      </Header>
      <Content style={{ padding: isMobile ? '12px' : '24px', background: theme.background }}>
        <Spin spinning={loading}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Office Info Display */}
            <Card
              title={<Text style={{ color: theme.text, fontSize: isMobile ? 14 : 16, fontWeight: 'bold' }}>إعدادات العرض</Text>}
              style={{ background: theme.surface, borderColor: theme.border }}
              headStyle={{ background: theme.surfaceLight, borderBottom: `1px solid ${theme.border}` }}
              bodyStyle={{ background: theme.surface, padding: isMobile ? 12 : undefined }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {/* Logo Upload */}
                <div>
                  <Text style={{ color: theme.text }}>رفع الشعار (Logo):</Text>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          // Validate file
                          const validation = api.validateMediaFile(file);
                          if (!validation.valid) {
                            message.error(validation.error || 'ملف غير صالح');
                            return;
                          }
                          message.loading('جاري رفع الشعار...', 0);
                          // Upload to server
                          const response = await api.uploadMedia(file, api.MediumFor.LOGO, false);
                          message.destroy();
                          message.success('تم رفع الشعار بنجاح');
                          // Update state with URL and ID
                          setDisplaySettings(prev => ({
                            ...prev,
                            logo: response.url,
                            logoId: response.id,
                          }));
                        } catch (error: any) {
                          message.destroy();
                          message.error('فشل في رفع الشعار: ' + error.message);
                        }
                      }
                    }}
                    style={{ marginTop: 8 }}
                  />
                  {displaySettings.logo && (
                    <div style={{ marginTop: 10 }}>
                      <img
                        src={displaySettings.logo}
                        alt="شعار المكتب"
                        style={{
                          maxWidth: 150,
                          maxHeight: 150,
                          objectFit: 'contain',
                          borderRadius: 8,
                          border: `1px solid ${theme.border}`,
                          background: theme.surfaceLight,
                          display: 'block',
                          margin: '0 auto'
                        }}
                      />
                      <Button
                        danger
                        size="small"
                        onClick={() => setDisplaySettings(prev => ({ ...prev, logo: '', logoId: null }))}
                        style={{ marginTop: 8, display: 'block', margin: '8px auto 0' }}
                      >
                        حذف الشعار
                      </Button>
                    </div>
                  )}
                </div>
                {/* Main Video Upload */}
                <div>
                  <Text style={{ color: theme.text }}>رفع الفيديو الأساسي (Main Video):</Text>
                  <Input
                    type="file"
                    accept="video/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          // Validate file (no size limit for video)
                          const validation = api.validateMediaFile(file, { skipSizeCheck: true });
                          if (!validation.valid) {
                            message.error(validation.error || 'ملف غير صالح');
                            return;
                          }
                          message.loading('جاري رفع الفيديو...', 0);
                          // Upload to server
                          const response = await api.uploadMedia(file, api.MediumFor.VIDEO, false);
                          message.destroy();
                          message.success('تم رفع الفيديو بنجاح');
                          // Update state with URL and ID
                          setDisplaySettings(prev => ({
                            ...prev,
                            video: response.url,
                            videoId: response.id,
                          }));
                        } catch (error: any) {
                          message.destroy();
                          message.error('فشل في رفع الفيديو: ' + error.message);
                        }
                      }
                    }}
                    style={{ marginTop: 8 }}
                  />
                  {displaySettings.video && (
                    <div style={{ marginTop: 10 }}>
                      <video
                        src={displaySettings.video}
                        style={{
                          maxWidth: 250,
                          maxHeight: 150,
                          objectFit: 'contain',
                          borderRadius: 8,
                          border: `1px solid ${theme.border}`,
                          background: theme.surfaceLight,
                          display: 'block',
                          margin: '0 auto'
                        }}
                        controls
                        muted
                      />
                      <Button
                        danger
                        size="small"
                        onClick={() => setDisplaySettings(prev => ({ ...prev, video: '', videoId: null }))}
                        style={{ marginTop: 8, display: 'block', margin: '8px auto 0' }}
                      >
                        حذف الفيديو
                      </Button>
                    </div>
                  )}
                </div>
                {/* Extra/Secondary Video Upload */}
                <div>
                  <Text style={{ color: theme.text }}>رفع فيديو إضافي (Video Option):</Text>
                  <Input
                    type="file"
                    accept="video/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          // Validate file (no size limit for video)
                          const validation = api.validateMediaFile(file, { skipSizeCheck: true });
                          if (!validation.valid) {
                            message.error(validation.error || 'ملف غير صالح');
                            return;
                          }
                          message.loading('جاري رفع الفيديو...', 0);
                          // Upload to server
                          const response = await api.uploadMedia(file, api.MediumFor.VIDEO, false);
                          message.destroy();
                          message.success('تم رفع الفيديو بنجاح');
                          // Update state with URL and ID
                          setDisplaySettings(prev => ({
                            ...prev,
                            videoOption: response.url,
                            videoOptionId: response.id,
                          }));
                        } catch (error: any) {
                          message.destroy();
                          message.error('فشل في رفع الفيديو: ' + error.message);
                        }
                      }
                    }}
                    style={{ marginTop: 8 }}
                  />
                  {displaySettings.videoOption && (
                    <div style={{ marginTop: 10 }}>
                      <video
                        src={displaySettings.videoOption}
                        style={{
                          maxWidth: 250,
                          maxHeight: 150,
                          objectFit: 'contain',
                          borderRadius: 8,
                          border: `1px solid ${theme.border}`,
                          background: theme.surfaceLight,
                          display: 'block',
                          margin: '0 auto'
                        }}
                        controls
                        muted
                      />
                      <Button
                        danger
                        size="small"
                        onClick={() => setDisplaySettings(prev => ({ ...prev, videoOption: '', videoOptionId: null }))}
                        style={{ marginTop: 8, display: 'block', margin: '8px auto 0' }}
                      >
                        حذف الفيديو
                      </Button>
                    </div>
                  )}
                </div>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveSettings}
                  loading={savingSettings}
                  style={{ background: theme.primary, borderColor: theme.primary }}
                >
                  حفظ الإعدادات
                </Button>
              </Space>
            </Card>

            {/* Currencies */}
            <Card
              title={
                <div>
                  <Text style={{ color: theme.text, fontWeight: 'bold' }}>العملات - Currencies</Text>
                </div>
              }
              style={{ background: theme.surface, borderColor: theme.border }}
              headStyle={{ background: theme.surfaceLight, borderBottom: `1px solid ${theme.border}` }}
              bodyStyle={{ background: theme.surface }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space wrap>
                  <Button icon={<PlusOutlined />} onClick={handleAddCurrency} type="primary">
                    إضافة عملة
                  </Button>
                  <Button
                    onClick={handleResetDefaults}
                    loading={savingCurrencies}
                    style={{ background: theme.warning, borderColor: theme.warning, color: '#fff' }}
                  >
                    القيم الافتراضية
                  </Button>

                </Space>
                {isMobile ? (
                  /* Mobile: card-based layout for currencies */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {currencies.map((record, index) => (
                      <div
                        key={record.id}
                        style={{
                          background: theme.surfaceLight,
                          border: `1px solid ${isCurrencyModified(record.id) ? theme.primary : theme.border}`,
                          borderRadius: 10,
                          padding: 12,
                        }}
                      >
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                          <div style={{ flex: '0 0 70px' }}>
                            <Text style={{ color: theme.textSecondary, fontSize: 11 }}>FLAG</Text>
                            <Input value={record.flag} onChange={(e) => updateCurrencyField(record.id, 'flag', e.target.value)} placeholder="us" size="small" />
                          </div>
                          <div style={{ flex: '0 0 70px' }}>
                            <Text style={{ color: theme.textSecondary, fontSize: 11 }}>ISO</Text>
                            <Input value={record.iso} onChange={(e) => updateCurrencyField(record.id, 'iso', e.target.value)} placeholder="USD" size="small" />
                          </div>
                          <div style={{ flex: 1, minWidth: 100 }}>
                            <Text style={{ color: theme.textSecondary, fontSize: 11 }}>NAME (EN)</Text>
                            <Input value={record.nameEn} onChange={(e) => updateCurrencyField(record.id, 'nameEn', e.target.value)} size="small" />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                          <div style={{ flex: 1 }}>
                            <Text style={{ color: theme.textSecondary, fontSize: 11 }}>NAME (AR)</Text>
                            <Input value={record.nameAr} onChange={(e) => updateCurrencyField(record.id, 'nameAr', e.target.value)} style={{ direction: 'rtl' }} size="small" />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                          <div style={{ flex: 1 }}>
                            <Text style={{ color: '#ef4444', fontSize: 11, fontWeight: 700 }}>BUY</Text>
                            <InputNumber value={record.buy} onChange={(val) => updateCurrencyField(record.id, 'buy', val || 0)} min={0} step={0.01} style={{ width: '100%' }} size="small" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <Text style={{ color: '#22c55e', fontSize: 11, fontWeight: 700 }}>SELL</Text>
                            <InputNumber value={record.sell} onChange={(val) => updateCurrencyField(record.id, 'sell', val || 0)} min={0} step={0.01} style={{ width: '100%' }} size="small" />
                          </div>
                        </div>
                        <Space size="small" wrap>
                          {isCurrencyModified(record.id) && record.id !== 0 && (
                            <Button type="primary" icon={updatingCurrencyId === record.id ? <LoadingOutlined /> : <EditOutlined />} onClick={() => handleUpdateCurrency(record)} disabled={updatingCurrencyId === record.id} size="small" style={{ background: theme.success, borderColor: theme.success }}>حفظ</Button>
                          )}
                          <Button icon={<ArrowUpOutlined />} onClick={() => handleMoveCurrency(index, 'up')} disabled={index === 0} size="small" />
                          <Button icon={<ArrowDownOutlined />} onClick={() => handleMoveCurrency(index, 'down')} disabled={index === currencies.length - 1} size="small" />
                          <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteCurrency(record.id)} size="small" />
                        </Space>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Table
                    dataSource={currencies}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                    style={{ background: theme.background }}
                    className="currency-table"
                    scroll={{ x: 900 }}
                  />
                )}
                <Space wrap size={isMobile ? 'small' : 'middle'} style={{ width: '100%', justifyContent: isMobile ? 'center' : undefined }}>
                  <Button
                    icon={<ColumnWidthOutlined />}
                    onClick={() => window.open(`/display/${currentOffice?.id || authOffice?.id}?h=1&theme=dark`, '_blank')}
                    style={{ background: theme.primary, borderColor: theme.primary, color: '#fff' }}
                    size={isMobile ? 'small' : 'middle'}
                  >
                    عرضي غامق
                  </Button>
                  <Button
                    icon={<ColumnWidthOutlined />}
                    onClick={() => window.open(`/display/${currentOffice?.id || authOffice?.id}?h=1&theme=light`, '_blank')}
                    style={{ background: theme.primary, borderColor: theme.primary, color: '#fff' }}
                    size={isMobile ? 'small' : 'middle'}
                  >
                    عرضي فاتح
                  </Button>
                  <Button
                    icon={<MenuOutlined />}
                    onClick={() => window.open(`/display/${currentOffice?.id || authOffice?.id}?v=1&theme=dark`, '_blank')}
                    style={{ background: theme.primary, borderColor: theme.primary, color: '#fff' }}
                    size={isMobile ? 'small' : 'middle'}
                  >
                    طولي غامق
                  </Button>
                  <Button
                    icon={<MenuOutlined />}
                    onClick={() => window.open(`/display/${currentOffice?.id || authOffice?.id}?v=1&theme=light`, '_blank')}
                    style={{ background: theme.primary, borderColor: theme.primary, color: '#fff' }}
                    size={isMobile ? 'small' : 'middle'}
                  >
                    طولي فاتح
                  </Button>
                </Space>
              </Space>
            </Card>
          </Space>
        </Spin>
      </Content>

      <style>{`
        @keyframes adminWaveBlinkBuy {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(-4px); opacity: 0.85; }
        }
        @keyframes adminWaveBlinkSell {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(-4px); opacity: 0.85; }
        }
        .admin-rate-cell.admin-rate-buy { animation: adminWaveBlinkBuy 2.5s ease-in-out infinite; }
        .admin-rate-cell.admin-rate-sell { animation: adminWaveBlinkSell 2.5s ease-in-out infinite; }

        /* Mobile responsive overrides */
        @media (max-width: 768px) {
          .ant-layout-header { line-height: normal !important; }
          .ant-card-body { padding: 12px !important; }
          .ant-table { font-size: 12px; }
          .ant-space { flex-wrap: wrap; }
          .ant-btn { font-size: 13px; }
        }

        /* PWA standalone mode - hide browser chrome areas */
        @media (display-mode: standalone) {
          body { padding-top: env(safe-area-inset-top); }
        }
      `}</style>
      <Modal
        title={editingCurrency?.id === 0 ? 'إضافة عملة جديدة' : 'تعديل العملة'}
        open={isModalVisible}
        onOk={handleSaveCurrency}
        onCancel={() => setIsModalVisible(false)}
        okText="حفظ"
        cancelText="إلغاء"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="flag"
            label="رمز العلم (Flag)"
            rules={[{ max: 255, message: 'يجب أن لا يتجاوز 255 حرف' }]}
          >
            <Input placeholder="us, gb, eu, sa, etc." />
          </Form.Item>
          <Form.Item
            name="nameEn"
            label="الاسم بالإنجليزية"
            rules={[
              { required: true, message: 'يرجى إدخال الاسم بالإنجليزية' },
              { max: 255, message: 'يجب أن لا يتجاوز 255 حرف' }
            ]}
          >
            <Input placeholder="US Dollar" />
          </Form.Item>
          <Form.Item
            name="nameAr"
            label="الاسم بالعربية"
            rules={[
              { required: true, message: 'يرجى إدخال الاسم بالعربية' },
              { max: 255, message: 'يجب أن لا يتجاوز 255 حرف' }
            ]}
          >
            <Input placeholder="دولار أمريكي" style={{ direction: 'rtl' }} />
          </Form.Item>
          <Form.Item
            name="iso"
            label="رمز العملة (ISO)"
            rules={[
              { required: true, message: 'يرجى إدخال رمز العملة' },
              { max: 10, message: 'يجب أن لا يتجاوز 10 أحرف' }
            ]}
          >
            <Input placeholder="USD" />
          </Form.Item>
          <Form.Item
            name="buy"
            label="سعر الشراء"
            rules={[{ required: true, message: 'يرجى إدخال سعر الشراء' }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>
          <Form.Item
            name="sell"
            label="سعر البيع"
            rules={[{ required: true, message: 'يرجى إدخال سعر البيع' }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Dashboard;
